import React, { useState, useEffect } from 'react';
import { Button, FormControl, FormGroup, FormHelperText, CircularProgress } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { DatePicker } from '../../../Common/DatePicker/DatePicker';
import { defaultTestDateValidation, validateForm, getWarningMessage, getUtcDateString, validateTestDate, TestDate } from '../TestDate.helper';
import { ModalCustomerSelector } from '../../../Common/CustomerSelector';
import { AcademicYearDTO } from '@beable/roster-backend-client-v1';
import SiteSelector from '../../../Common/SiteSelector';
import ProductSelectList from '../../../Common/Selectors/ProductSelectList';
import AcademicYearSelector from '../../../Common/AcademicYearSelector';
import TestAheadCourseCodeSelector from '../../../Common/Selectors/TestAheadCourseCodeSelector';
import testDateService from '../../../../services/TestDateService';
import FormModal, { getResultMessage, ModalMessageSettings } from '../../../Common/FormModal';
import { getStyles } from '../styles';

const ModalSiteSelector = styled(SiteSelector)(({ theme }) => getStyles(theme).modalSelector);
const ModalProductSelectList = styled(ProductSelectList)(({ theme }) => getStyles(theme).modalSelector);
const ModalAcademicYearSelector = styled(AcademicYearSelector)(({ theme }) => getStyles(theme).modalSelector);
const ModalTestAheadCourseCodeSelector = styled(TestAheadCourseCodeSelector)(({ theme }) => getStyles(theme).modalSelector);
interface EditTestDateModalProps {
    open: boolean;
    testDate: TestDate,
    handleClose: (testDateHasChange: boolean) => void;
}
export default function EditTestDateModal(props: EditTestDateModalProps) {
    const theme = useTheme();
    const styles = getStyles(theme);
    const [isLoading, setIsLoading] = useState(false);
    const [validation, setValidation] = useState(defaultTestDateValidation);
    const [modalMessage, setModalMessage] = useState<ModalMessageSettings>();
    const [shouldEnableSaveButton, setShouldEnableSaveButton] = useState<boolean>(false);
    const [academicYearId, setAcademicYearId] = useState(props.testDate.academicYearId);
    const [courseCode, setCourseCode] = useState(props.testDate.courseCode);
    const [testDate, setTestDate] = useState<string>('');
    const [testDateHasChange, setTestDateHasChange] = useState<boolean>(false);
    const [dateWarning, setDateWarning] = useState<string>();
    const [success, setSuccess] = useState(false);
 
    useEffect(() => {
        if(props.testDate) {
            const dateString = getUtcDateString(props.testDate.testDate);
            setDateWarning(getWarningMessage(dateString, dateString));
            setTestDate(dateString);
            setAcademicYearId(props.testDate.academicYearId);
            setCourseCode(props.testDate.courseCode);
            setValidation(defaultTestDateValidation);
        }
    }, []);
    useEffect(
        () => {
            const validation = defaultTestDateValidation;
            
            if (!testDate) {
                setTestDate(getUtcDateString(props.testDate.testDate));
            }
            if (testDate) {
                validation.testDate = validateTestDate(getUtcDateString(testDate), getUtcDateString(props.testDate.testDate)).dateError;
                setValidation(validation);
            }
            if(!academicYearId || !courseCode || !testDate || validation.testDate.length) {
                setShouldEnableSaveButton(false);
            } else {
                setShouldEnableSaveButton(true);
            }
        },
        [ academicYearId, courseCode, testDate, validation.testDate ]
    );
    const submitForm = async (_event: any) => {
        const validation = await validateForm(
            props.testDate.customerId, 
            props.testDate.siteId, 
            props.testDate.productId, 
            academicYearId, 
            courseCode, 
            testDate,
            getUtcDateString(props.testDate.testDate)
        );
        if (validation.isInvalid) {
            setValidation(validation.validation);          
        } else {
            setValidation(defaultTestDateValidation);
            setIsLoading(true);
            const { validation, error, success } = await testDateService.saveTestDate(
                props.testDate.customerId, 
                props.testDate.siteId, 
                props.testDate.productId, 
                academicYearId, 
                courseCode, 
                testDate, 
                props.testDate.id,
                getUtcDateString(props.testDate.testDate)
            );
            setIsLoading(false);
            if(validation && validation.isInvalid) {                
                setValidation(validation.validation);
            } else {
                const message = getResultMessage(success ? true : undefined, success, error);
                setSuccess(!!success);
                setModalMessage(message);
            }
        }
    };
    const handleDateChange = (selectedDate: Date) => {
        const dateString = getUtcDateString(selectedDate);
        if(dateString !== getUtcDateString(props.testDate.testDate)) {
            setTestDateHasChange(true);
            setTestDate(dateString);
        } else if (props.testDate.academicYearId === academicYearId && props.testDate.courseCode === props.testDate.courseCode) {
            setTestDateHasChange(false);
        }
        setDateWarning(getWarningMessage(dateString, getUtcDateString(props.testDate.testDate)));
    }
    
    const handleAcademicYearIdChange = (selectedAcademicYear: AcademicYearDTO | undefined) => {
        setTestDateHasChange(selectedAcademicYear?.academicYearId !== academicYearId);
        if (selectedAcademicYear?.academicYearId) {
            setAcademicYearId(selectedAcademicYear?.academicYearId);
        }
    }
    const handleCourseCodeChange = (selectedCourseCode: string) => {
        setTestDateHasChange(selectedCourseCode !== props.testDate.courseCode);
        setCourseCode(selectedCourseCode);
    }

    return (
        <FormModal
            title="Change test date"
            description="Use the form below to edit the test date."
            isOpen={props.open}
            handleClose={() => {
                props.handleClose(false);
                setModalMessage(undefined);
            }}
            message={modalMessage}
        >
            <div>
                <FormGroup sx={{ alignItems: 'stretch' }}>
                    <FormControl variant="standard" fullWidth sx={[styles.modalSelector, styles.mb2]}>
                        <DatePicker
                            label='Test Date'
                            value={testDate ? new Date(testDate) : null}
                            onChange={handleDateChange}
                            minDate={new Date()}
                        />
                        {dateWarning ? <FormHelperText sx={styles.redTxt}>{dateWarning}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalCustomerSelector
                            customerId={props.testDate.customerId}
                            onSelect={() => {}}
                            disabled
                        />
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalSiteSelector
                            siteId={props.testDate.siteId}
                            customerId={props.testDate.customerId}
                            onSelect={() => {}}
                            disabled
                        />
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalProductSelectList
                            productId={props.testDate.productId}
                            customerId={props.testDate.customerId}
                            siteId={props.testDate.siteId}
                            onChange={() => {}}
                            disabled
                        />
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalAcademicYearSelector
                            academicYearId={academicYearId}
                            siteId={props.testDate.siteId}
                            handleSelection={handleAcademicYearIdChange}
                            disabled={!props.testDate.siteId}
                        />
                        {validation.academicYearId ? <FormHelperText sx={styles.redTxt}>{validation.academicYearId}</FormHelperText> : undefined}
                    </FormControl>
                    <FormControl variant="standard" fullWidth>
                        <ModalTestAheadCourseCodeSelector
                            courseCode={courseCode}
                            onChange={handleCourseCodeChange}
                        />
                        {validation.courseCode ? <FormHelperText sx={styles.redTxt}>{validation.courseCode}</FormHelperText> : undefined}
                    </FormControl>
                </FormGroup>
                <FormGroup>
                    {isLoading ? <CircularProgress color="primary" /> : undefined}
                </FormGroup>
                <FormGroup sx={[styles.mt2, styles.dfr]}>
                    <Button
                        variant="contained"
                        color="secondary"
                        disabled={!shouldEnableSaveButton || !testDateHasChange}
                        onClick={submitForm}
                    >
                        Save changes
                    </Button>
                    <Button
                        variant="contained"
                        onClick={(event) => props.handleClose(success)}
                        sx={styles.ml2}
                    >
                        Close
                    </Button>
                </FormGroup>
            </div>
        </FormModal>
    );
}